/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import { updateQueryStringParameter } from 'yoda-core-components/lib/helpers/Utils/Utils';
import { isSearchPage } from '../../../../helpers/Utils/pageType';
import {
    isBloomReachAudience,
    enableBloomReachPage,
} from '../../../../helpers/BloomReach/BloomReach';
import { setOnClickBeaconStatus } from '../../../../actions/CriteoAction';
import Image from './Image';
import styles from './Swatches.css';

const cx = classNames.bind(styles);

const Swatches = ({
    activeSwatchImageId,
    handleSwatchDesktopEvent,
    handleSwatchMobileEvent,
    isEnsembledProduct,
    productCardWidth,
    productUrl,
    setGalleryScrollInLocalStorage,
    skuSwatch: swatches,
    productCardRef,
    desktopWidth,
    OnClickBeacon,
    onClickBeaconCalledPPIDs,
    skuId,
}) => {
    const {
        featureFlags: {
            enableSwatches = true,
            enableBloomReachV3PLP = true,
            enableBloomReach = false,
            enableBloomReachNative = false,
            enableMobileSwatchTap = false,
        } = {},
        preferences: { swatches: { itemsPerRow = 6 } = {}, plpBloomreachInclusionList = [] } = {},
        requestUrl = '',
        isNative = false,
    } = useSelector(({ context = {} }) => context);
    const dispatch = useDispatch();

    const productCardWidthDt = productCardRef?.current?.clientWidth || productCardWidth;
    const isBRAudience = useMemo(() => {
        const isNativeBloomReach = enableBloomReachNative ? false : isNative;
        const isBloomReachAudienceABTest =
            isBloomReachAudience(enableBloomReach, isNativeBloomReach) && isSearchPage(requestUrl);
        const isBloomReachV3Page = enableBloomReachPage(
            enableBloomReachV3PLP,
            requestUrl,
            plpBloomreachInclusionList
        );
        return isBloomReachAudienceABTest || isBloomReachV3Page;
    }, [
        enableBloomReachNative,
        enableBloomReach,
        requestUrl,
        enableBloomReachV3PLP,
        plpBloomreachInclusionList,
    ]);

    const [maxSwatchesPerRow, setMaxSwatchesPerRow] = useState(isBRAudience ? 12 : itemsPerRow);
    const [activeSwatch, setActiveSwatch] = useState(null);
    const swatchesToShow = swatches.slice(
        0,
        // Account for extra swatches button
        swatches.length > maxSwatchesPerRow ? maxSwatchesPerRow - 1 : maxSwatchesPerRow
    );

    const swatchSize = 20;

    useEffect(() => {
        if (productCardWidthDt > 0) {
            const swatchesPerRow = Math.floor(productCardWidthDt / (swatchSize + 8)) || itemsPerRow;
            if (swatchesPerRow !== maxSwatchesPerRow) {
                setMaxSwatchesPerRow(isBRAudience && swatchesPerRow > 12 ? 12 : swatchesPerRow);
            }
        }
    }, [productCardWidthDt]);

    useEffect(() => {
        const nextActiveSwatch = swatchesToShow.find(
            (swatch) => swatch.swatchImageId === activeSwatchImageId
        );

        nextActiveSwatch && setActiveSwatch(nextActiveSwatch);
    }, [activeSwatchImageId, maxSwatchesPerRow, swatchesToShow]);

    const navigateToPdp = () => {
        const previouslySelectedSwatchId = activeSwatch?.skuId ?? '';
        let pdpUrl = productUrl;

        if (desktopWidth && previouslySelectedSwatchId?.length) {
            pdpUrl = updateQueryStringParameter(
                productUrl,
                'selectedSKUId',
                previouslySelectedSwatchId
            );
        }
        /* istanbul ignore if */
        if (OnClickBeacon && !onClickBeaconCalledPPIDs?.includes(skuId)) {
            navigator.sendBeacon(`https:${OnClickBeacon}`);
            dispatch(setOnClickBeaconStatus(skuId));
        }
        LocalStorage.setData('fromGallery', { scrollTo: 'swatch' });
        setGalleryScrollInLocalStorage();
        window.location.assign(pdpUrl);
    };

    const visibleSwatches = useMemo(() => {
        return swatchesToShow.map((currentSwatch, index) => {
            const { colorName, swatchImageId } = currentSwatch;
            const isSwatchSelected = activeSwatch && swatchImageId === activeSwatch?.swatchImageId;
            const config = {
                button: {
                    className: `${dt(['ml-2', 'relative'])} ${cx({
                        active: isSwatchSelected,
                    })}`,
                    key: `${colorName}-${swatchImageId}-${index}`,
                    onClick:
                        enableMobileSwatchTap || desktopWidth
                            ? (event) => handleSwatchDesktopEvent(event, currentSwatch)
                            : () => handleSwatchMobileEvent(currentSwatch),
                    onFocus:
                        enableMobileSwatchTap || desktopWidth
                            ? (event) => handleSwatchDesktopEvent(event, currentSwatch)
                            : null,
                    onMouseLeave:
                        enableMobileSwatchTap || desktopWidth
                            ? (event) =>
                                  handleSwatchDesktopEvent(
                                      event,
                                      isEnsembledProduct ? currentSwatch : activeSwatch
                                  )
                            : null,
                    onMouseOver:
                        enableMobileSwatchTap || desktopWidth
                            ? (event) => handleSwatchDesktopEvent(event, currentSwatch)
                            : null,
                    style: {
                        height: swatchSize,
                        width: swatchSize,
                    },
                },
                image: {
                    alt: colorName,
                    iconSize: swatchSize,
                    isSwatchSelected,
                    swatchImageId,
                },
            };

            return (
                <button
                    {...config?.button}
                    data-value={isSwatchSelected ? 'active' : 'not-active'}
                    key={swatchImageId}
                    type="button"
                >
                    <Image {...config?.image} />
                </button>
            );
        });
    }, [maxSwatchesPerRow, swatches, swatchesToShow]);

    if (enableSwatches && swatches?.length) {
        const showExtraSwatchesButton = swatches?.length > maxSwatchesPerRow;
        const swatchesConfig = {
            extraSwatches: {
                button: {
                    'aria-label': isBRAudience
                        ? `View additional swatches on product page`
                        : `View ${
                              swatches?.length - maxSwatchesPerRow
                          } additional swatches on product page`,
                    className: `${dt([
                        'flex',
                        'font-sans',
                        'items-center',
                        'mx-2',
                        'text-xs',
                    ])} ${cx('additional-swatches')}`,
                    onClick: navigateToPdp,
                    style: {
                        height: swatchSize,
                        width: swatchSize,
                    },
                },
                icon: {
                    className: dt(['font-open-sans', isBRAudience ? 'text-3xlarge' : 'text-md']),
                },
            },
            wrapper: {
                className: `${dt(['flex', 'relative', 'pt-2', 'smOnly:pt-1', 'mdOnly:pt-1'])} ${cx(
                    'swatches'
                )}`,
                'data-automation-id': 'color-availability',
            },
        };

        return (
            <div {...swatchesConfig?.wrapper}>
                {visibleSwatches}
                {showExtraSwatchesButton && (
                    <button {...swatchesConfig?.extraSwatches?.button} type="button">
                        <span {...swatchesConfig?.extraSwatches?.icon}>+</span>
                        {isBRAudience ? null : swatches?.length - (maxSwatchesPerRow - 1)}
                    </button>
                )}
            </div>
        );
    }

    return null;
};

Swatches.propTypes = {
    activeSwatchImageId: PropTypes.string.isRequired,
    handleSwatchDesktopEvent: PropTypes.func.isRequired,
    handleSwatchMobileEvent: PropTypes.func.isRequired,
    isEnsembledProduct: PropTypes.bool.isRequired,
    setGalleryScrollInLocalStorage: PropTypes.func.isRequired,
    skuSwatch: PropTypes.arrayOf(
        PropTypes.shape({
            colorName: PropTypes.string,
            colorizedImageId: PropTypes.string,
            defaultSwatch: PropTypes.bool,
            skuAltImages: PropTypes.arrayOf(
                PropTypes.shape({
                    imgId: PropTypes.string,
                    colorSeq: PropTypes.number,
                })
            ),
            skuId: PropTypes.string,
            swatchImageId: PropTypes.string,
        })
    ).isRequired,
    productCardWidth: PropTypes.number,
    productUrl: PropTypes.string,
    desktopWidth: PropTypes.bool,
};

Swatches.defaultProps = {
    productCardWidth: 0,
    productUrl: '',
    desktopWidth: false,
};

export default React.memo(Swatches);
